import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Plus, Pencil, Trash2, X, Globe, Loader2, AlertTriangle, CheckCircle, Info, BarChart3 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const QUALITY_COLORS = {
  high: 'bg-emerald-100 text-emerald-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-red-100 text-red-800',
};
const QUALITY_LABELS = { high: 'Yuksek', medium: 'Orta', low: 'Dusuk' };

export default function LandingPagesPage() {
  const [landings, setLandings] = useState([]);
  const [cities, setCities] = useState([]);
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    city_id: '', service_id: '', h1: '', intro_html: '',
    content_sections: { service_explanation: '', city_context: '', cta_text: '' },
    faq: [], meta_title: '', meta_description: '', og_image: '', is_published: false
  });
  const [faqQ, setFaqQ] = useState('');
  const [faqA, setFaqA] = useState('');
  const [publishWarning, setPublishWarning] = useState(null);

  const loadData = () => {
    setLoading(true);
    Promise.all([
      adminAPI.getLandingPages(),
      adminAPI.getCities(),
      adminAPI.getServices(),
      adminAPI.getLandingStats()
    ]).then(([lpRes, ctRes, svcRes, statsRes]) => {
      setLandings(lpRes.data);
      setCities(ctRes.data);
      setServices(svcRes.data);
      setStats(statsRes.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const resetForm = () => {
    setForm({ city_id: '', service_id: '', h1: '', intro_html: '', content_sections: { service_explanation: '', city_context: '', cta_text: '' }, faq: [], meta_title: '', meta_description: '', og_image: '', is_published: false });
    setEditing(null);
    setShowForm(false);
    setPublishWarning(null);
  };

  const startEdit = (lp) => {
    setForm({
      city_id: lp.city_id, service_id: lp.service_id, h1: lp.h1 || '',
      intro_html: lp.intro_html || '',
      content_sections: lp.content_sections || { service_explanation: '', city_context: '', cta_text: '' },
      faq: lp.faq || [],
      meta_title: lp.meta_title || '', meta_description: lp.meta_description || '',
      og_image: lp.og_image || '', is_published: lp.is_published || false
    });
    setEditing(lp.id);
    setShowForm(true);
  };

  const addFaq = () => {
    if (!faqQ.trim() || !faqA.trim()) return;
    setForm(f => ({ ...f, faq: [...f.faq, { question: faqQ, answer: faqA }] }));
    setFaqQ('');
    setFaqA('');
  };

  const removeFaq = (i) => {
    setForm(f => ({ ...f, faq: f.faq.filter((_, idx) => idx !== i) }));
  };

  const updateSection = (key, value) => {
    setForm(f => ({ ...f, content_sections: { ...f.content_sections, [key]: value } }));
  };

  // Compute local word count
  const getWordCount = () => {
    let text = (form.intro_html || '').replace(/<[^>]+>/g, '');
    Object.values(form.content_sections || {}).forEach(v => {
      if (typeof v === 'string') text += ' ' + v.replace(/<[^>]+>/g, '');
    });
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  const wordCount = getWordCount();
  const localQuality = wordCount >= 500 ? 'high' : wordCount >= 200 ? 'medium' : 'low';

  const handlePublishToggle = async (val) => {
    if (val && !form.is_published) {
      // Check if publishing, show warning if quality is low
      if (wordCount < 500) {
        const confirmed = window.confirm(
          `Icerik kalitesi dusuk (${wordCount} kelime). Minimum 500-800 kelime onerilir.\n\nYine de yayinlamak istiyor musunuz?`
        );
        if (!confirmed) return;
      }
    }
    setForm(f => ({ ...f, is_published: val }));
  };

  const handleSubmit = async () => {
    if (!editing && (!form.city_id || !form.service_id)) { toast.error('Sehir ve hizmet secin'); return; }
    
    // Bulk publish warning
    if (form.is_published && !editing) {
      try {
        const check = await adminAPI.bulkPublishCheck(1);
        if (check.data.has_warnings) {
          setPublishWarning(check.data);
          return;
        }
      } catch {}
    }
    
    await doSubmit();
  };

  const doSubmit = async () => {
    try {
      if (editing) {
        await adminAPI.updateLandingPage(editing, form);
        toast.success('Landing page guncellendi');
      } else {
        await adminAPI.createLandingPage(form);
        toast.success('Landing page olusturuldu');
      }
      resetForm();
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata olustu'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu landing page silinsin mi?')) return;
    try {
      await adminAPI.deleteLandingPage(id);
      toast.success('Silindi');
      loadData();
    } catch { toast.error('Silinemedi'); }
  };

  return (
    <div data-testid="admin-landing-pages">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Landing Pages</h1>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-landing-btn">
          <Plus className="h-4 w-4 mr-2" /> Yeni Landing
        </Button>
      </div>

      {/* SEO Growth Stats */}
      {stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Toplam Sayfa</p>
              <p className="text-2xl font-semibold tabular-nums">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Yayinda</p>
              <p className="text-2xl font-semibold tabular-nums text-green-700">{stats.published}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Mevcut Faz</p>
              <p className="text-xl font-semibold">{stats.phase_label}</p>
              <p className="text-xs text-muted-foreground">{stats.published}/{stats.phase_limit} limit</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Icerik Kalitesi</p>
              <div className="flex gap-2 mt-1">
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 text-xs">{stats.quality_breakdown?.high || 0} Yuksek</Badge>
                <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-xs">{stats.quality_breakdown?.medium || 0} Orta</Badge>
                <Badge variant="secondary" className="bg-red-100 text-red-800 text-xs">{stats.quality_breakdown?.low || 0} Dusuk</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* SEO Growth Phases Info */}
      {stats && stats.remaining_in_phase === 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid="phase-limit-warning">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-900">Faz limiti doldu</p>
            <p className="text-sm text-amber-700 mt-1">
              {stats.phase_label} limiti ({stats.phase_limit} sayfa) dolmustur. Yeni sayfalar ekleyebilirsiniz ancak kademeli buyume stratejisi izlemeniz onerilir.
              Sonraki faz: {stats.current_phase === 'phase_1' ? 'Faz 2 (100 sayfa)' : stats.current_phase === 'phase_2' ? 'Faz 3 (300 sayfa)' : 'Sinirsiz'}
            </p>
          </div>
        </div>
      )}

      {/* Publish Warning Modal */}
      {publishWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="bulk-publish-warning">
          <Card className="max-w-lg w-full mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-5 w-5" /> SEO Uyarisi
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {publishWarning.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 text-sm p-3 bg-amber-50 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span className="text-amber-900">{w}</span>
                </div>
              ))}
              <p className="text-sm text-muted-foreground">
                Mevcut yayinda: {publishWarning.current_published} sayfa
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setPublishWarning(null)}>Iptal</Button>
                <Button onClick={() => { setPublishWarning(null); doSubmit(); }}>Yine de Yayinla</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showForm && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{editing ? 'Landing Duzenle' : 'Yeni Landing Page'}</CardTitle>
            <button onClick={resetForm}><X className="h-5 w-5" /></button>
          </CardHeader>
          <CardContent className="space-y-4">
            {!editing && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Sehir *</Label>
                  <select value={form.city_id} onChange={e => setForm(f => ({...f, city_id: e.target.value}))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="landing-city-select">
                    <option value="">Sehir secin</option>
                    {cities.filter(c => c.is_active).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Hizmet *</Label>
                  <select value={form.service_id} onChange={e => setForm(f => ({...f, service_id: e.target.value}))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="landing-service-select">
                    <option value="">Hizmet secin</option>
                    {services.filter(s => s.is_active).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
            )}
            <div><Label>H1 Baslik</Label><Input value={form.h1} onChange={e => setForm(f => ({...f, h1: e.target.value}))} data-testid="landing-h1-input" /></div>
            <div>
              <Label>Giris Metni (HTML)</Label>
              <textarea value={form.intro_html} onChange={e => setForm(f => ({...f, intro_html: e.target.value}))} rows={3} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            
            {/* Content Sections */}
            <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-sm">Icerik Bolumleri</h3>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className={`${QUALITY_COLORS[localQuality]} text-xs`} data-testid="word-count-badge">
                    {wordCount} kelime - {QUALITY_LABELS[localQuality]}
                  </Badge>
                </div>
              </div>
              
              {wordCount < 500 && (
                <div className="flex items-start gap-2 p-2 bg-amber-50 rounded text-xs text-amber-800" data-testid="content-warning">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>Minimum 500-800 kelime onerilir. Suan {wordCount} kelime mevcut.</span>
                </div>
              )}
              
              <div>
                <Label className="text-xs">Hizmet Aciklamasi</Label>
                <textarea value={form.content_sections.service_explanation || ''} onChange={e => updateSection('service_explanation', e.target.value)} rows={3} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Bu hizmet hakkinda detayli aciklama..." />
              </div>
              <div>
                <Label className="text-xs">Sehir Baglami</Label>
                <textarea value={form.content_sections.city_context || ''} onChange={e => updateSection('city_context', e.target.value)} rows={3} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Sehrin endustriyel baglami..." />
              </div>
              <div>
                <Label className="text-xs">CTA Metni</Label>
                <Input value={form.content_sections.cta_text || ''} onChange={e => updateSection('cta_text', e.target.value)} placeholder="Hemen teklif alin..." />
              </div>
            </div>

            {/* SEO Fields */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Meta Title</Label><Input value={form.meta_title} onChange={e => setForm(f => ({...f, meta_title: e.target.value}))} /></div>
              <div><Label>Meta Description</Label><Input value={form.meta_description} onChange={e => setForm(f => ({...f, meta_description: e.target.value}))} /></div>
            </div>
            <div><Label>OG Image URL</Label><Input value={form.og_image} onChange={e => setForm(f => ({...f, og_image: e.target.value}))} /></div>
            
            {/* FAQ */}
            <div>
              <Label className="mb-2 block">SSS (Sik Sorulan Sorular)</Label>
              <div className="grid sm:grid-cols-2 gap-2 mb-2">
                <Input value={faqQ} onChange={e => setFaqQ(e.target.value)} placeholder="Soru" />
                <div className="flex gap-2">
                  <Input value={faqA} onChange={e => setFaqA(e.target.value)} placeholder="Cevap" />
                  <Button variant="outline" size="sm" onClick={addFaq}>Ekle</Button>
                </div>
              </div>
              {form.faq.map((item, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded mb-1 text-sm">
                  <span className="flex-1"><strong>S:</strong> {item.question} <strong>C:</strong> {item.answer}</span>
                  <button onClick={() => removeFaq(i)}><X className="h-4 w-4 text-red-500" /></button>
                </div>
              ))}
              {form.faq.length === 0 && (
                <p className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> SSS bolumu bos. En az 2-3 soru eklemeniz onerilir.</p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={form.is_published} onCheckedChange={handlePublishToggle} />
              <Label>Yayinda</Label>
              {form.is_published && wordCount < 500 && (
                <span className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Dusuk icerik kalitesiyle yayinlanacak</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSubmit} data-testid="landing-submit">{editing ? 'Guncelle' : 'Olustur'}</Button>
              <Button variant="outline" onClick={resetForm}>Iptal</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : landings.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Henuz landing page olusturulmamis</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {landings.map(lp => (
            <Card key={lp.id} data-testid={`landing-${lp.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Globe className="h-4 w-4 text-[hsl(221,83%,22%)] flex-shrink-0" />
                    <span className="font-semibold">{lp.city_name} / {lp.service_name}</span>
                    <Badge variant={lp.is_published ? 'default' : 'secondary'} className="text-xs">{lp.is_published ? 'Yayinda' : 'Taslak'}</Badge>
                    {lp.quality && (
                      <Badge variant="secondary" className={`text-xs ${QUALITY_COLORS[lp.quality.quality]}`}>
                        {lp.quality.word_count} kelime
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">/{lp.city_slug}/{lp.service_slug}</p>
                  {lp.quality && lp.quality.warnings && lp.quality.warnings.length > 0 && lp.is_published && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                      <AlertTriangle className="h-3 w-3" />
                      <span>{lp.quality.warnings[0]}</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(lp)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(lp.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
